import {
  Dialog,
  DialogFooter,
  DialogType,
  MessageBar,
  MessageBarType,
  PrimaryButton,
  TextField,
  Toggle,
} from "@fluentui/react";
import SettingsUtil from "../../Utils/Settings";
import "./OptionsPage.scss";
import { ToastMessage } from "../../Utils/UIMessages";
import { useEffect, useState, useCallback } from "react";
import CustomCommandBar from "../../Components/CustomCommandBar/CustomCommandBar";
import { useDispatch, useSelector } from "react-redux";
import { GlobalState } from "../../Redux/RootReducer";
import { IRole } from "../../Models/IRole";
import DataTable from "../../Components/DataTable/DataTable";
import { ISystemSetting } from "../../Models/ISystemSetting";
import ModalDialog from "../../Components/ModalDialog/ModalDialog";
import ApiService from "../../Services/ApiService";
import { AjaxService, IAPIRequest, IAPIResponse } from "../../Services/AjaxService";
import { GenericActions } from "../../Redux/Generic/GenericAction";
import { JSONPrint } from "../../Utils/Generic";
import NavigationMenu from "../../Components/NavigationMenu/NavigationMenu";
import { APIUrls } from "../../Constants/ApiUrls";
import OwnEmailPanel from "../../Components/OwnEmailPanel/OwnEmailPanel";
import PushPanel from "../../Components/PushPanel/PushPanel";

export const developerUI = () => {
  return localStorage.getItem("developer-ui") === "true";
};

export const DEFAULT_VIDEO_SIZES = [250, 120, 360, 220];
const OptionsPage = () => {
  const loggedUser = useSelector((s: GlobalState) => s.generic.user);
  const sysSettings = useSelector((s: GlobalState) => s.generic.systemSettings);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState<boolean>(false);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [openModalEdit, setOpenModalEdit] = useState<boolean>(false);
  const [targetSetting, setTargetSetting] = useState<ISystemSetting | null>(
    null
  );

  // PWA Install App state
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showInstallButton, setShowInstallButton] = useState<boolean>(false);

  var adminUI: boolean = false;
  if (
    loggedUser &&
    (loggedUser.roles.filter((x: IRole) => x.type === "superAdmin").length >
      0 ||
      loggedUser.roles.filter((x: IRole) => x.type === "installer").length > 0)
  ) {
    adminUI = true;
  }

  const [backendVersion, setBackendVersion] = useState<string>('...');
  const [reload, setReload] = useState<boolean>(false);
  const doReload = () => {
    setReload(true);
    setTimeout(() => {
      setReload(false);
    }, 50);
  };

  const updateSettingItem = (key: string, value: any) => {
    let sensorItem_: any = targetSetting ? { ...targetSetting } : {};
    sensorItem_[key] = value;
    setTargetSetting(sensorItem_);
  };

  const loadSysSettings = () => {
    ApiService.SystemSettingController.GetSettingsList(
      (response: IAPIResponse) => {
        if (response.error === null) {
          dispatch(GenericActions.setSysSettings(response.parsed) as any);
        }
      }
    );
  };

  const createOrEditSetting = () => {
    if (targetSetting) {
      setLoading(true);
      ApiService.SystemSettingController.CreateOrEditSetting(
        targetSetting,
        (response: IAPIResponse) => {
          if (response.error === null) {
            loadSysSettings();
            doReload();
            setOpenModal(false);
          } else {
            ToastMessage(
              "error",
              "Impossibile modificare o creare il parametro di sistema."
            );
          }
          setLoading(false);
        }
      );
    }
  };

  useEffect(() => {
    let request: IAPIRequest = AjaxService.GetEmptyRequest();
    request.url = APIUrls.VERSION_URL;
    AjaxService.APICall(request, (response: any) => {
      if (response.error === null && response.parsed?.version) {
        setBackendVersion(response.parsed.version);
      } else {
        setBackendVersion('n/d');
      }
    });
  }, []);

  // Installazione dell'app sul telefono o sul PC. L'offerta del browser la raccoglie index.tsx all'avvio.
  const isStandalone =
    window.matchMedia?.("(display-mode: standalone)").matches || (window.navigator as any).standalone === true;
  const isIOS = /iphone|ipad|ipod/i.test(window.navigator.userAgent);
  useEffect(() => {
    const pick = () => setDeferredPrompt((window as any).__installPrompt ?? null);
    pick();
    window.addEventListener("install-prompt-ready", pick);
    // gia' installata e aperta come app: il pulsante non serve
    setShowInstallButton(!isStandalone);
    return () => window.removeEventListener("install-prompt-ready", pick);
  }, []);

  // istruzioni in una finestra da chiudere, non in un avviso che sparisce da solo (titolare, 01/11/2026)
  const [installHelp, setInstallHelp] = useState<boolean>(false);
  const handleInstallClick = useCallback(async () => {
    if (!deferredPrompt) {
      setInstallHelp(true);
      return;
    }
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    ToastMessage(outcome === "accepted" ? "success" : "info",
      outcome === "accepted" ? "App installata: la trovi tra le app del telefono o del PC." : "Installazione annullata.");
    if (outcome === "accepted") setShowInstallButton(false);
    (window as any).__installPrompt = null;
    setDeferredPrompt(null);
  }, [deferredPrompt]);

  return (
    <div className="fade-in-whole-page">
      <CustomCommandBar
        specials={[
          <div className="command-bar-title">Impostazioni</div>,
          <NavigationMenu />,
        ]}
        buttons={[]}
      />

      <div className="sliding-page-content">
        {showInstallButton && (
          <div className="panel" style={{ marginBottom: "1em", maxWidth: "36em" }}>
            <div style={{ display: "flex", gap: "1em", alignItems: "center", flexWrap: "wrap" }}>
              <PrimaryButton
                iconProps={{ iconName: "Download" }}
                text="Installa App"
                onClick={handleInstallClick}
              />
            </div>
          </div>
        )}
        <Dialog
          hidden={!installHelp}
          onDismiss={() => setInstallHelp(false)}
          dialogContentProps={{
            type: DialogType.close,
            title: "Installa l'app",
            closeButtonAriaLabel: "Chiudi",
          }}
          modalProps={{ isBlocking: false }}
        >
          {window.location.protocol !== "https:" ? (
            <p>L'app si installa solo aprendo il sito dall'indirizzo che comincia con https.</p>
          ) : isIOS ? (
            <ol className="install-steps">
              <li>Apri questa pagina con <b>Safari</b>.</li>
              <li>Tocca il pulsante <b>Condividi</b> (il quadrato con la freccia in su), in basso.</li>
              <li>Scorri e tocca <b>«Aggiungi alla schermata Home»</b>, poi <b>Aggiungi</b>.</li>
              <li>Apri l'app dall'icona verde sulla schermata Home.</li>
            </ol>
          ) : (
            <ol className="install-steps">
              <li>Apri il menu del browser: i tre puntini <b>⋮</b> in alto a destra.</li>
              <li>Tocca <b>«Installa app»</b> oppure <b>«Aggiungi a schermata Home»</b>.</li>
              <li>Se la voce non c'è, l'app è già installata: cercala tra le app del telefono o del PC.</li>
            </ol>
          )}
          <DialogFooter>
            <PrimaryButton text="Ho capito" onClick={() => setInstallHelp(false)} />
          </DialogFooter>
        </Dialog>
        <div className="panel" style={{ marginBottom: "1em", maxWidth: "36em" }}>
          <PushPanel />
        </div>
        <div className="panel" style={{ marginBottom: "1em", maxWidth: "36em" }}>
          <OwnEmailPanel />
        </div>

        {/* SSL Cloud: le misure del video servono a chi installa, non a chi usa il sistema (titolare, 01/11/2026) */}
        {adminUI && (
        <details
          className="panel flex-column-on-small-screen options-video"
          style={{ marginBottom: "1em" }}
        >
          <summary>Misure del video (avanzate)</summary>
          {!reload && (
            <div className="options-page-line-input-wrap flex-column-on-small-screen">
              <div className="options-page-line-input fill-width-on-small-screen">
                <TextField
                  label="Larghezza del video (px)"
                  placeholder="Ampiezza Video"
                  type="number"
                  defaultValue={SettingsUtil.GetSettings(
                    "w_video_m",
                    DEFAULT_VIDEO_SIZES[0]
                  )}
                  onChange={(e: any, t: string | undefined) => {
                    if (t !== undefined) {
                      SettingsUtil.SetSettings("w_video_m", +t);
                    }
                  }}
                />
              </div>
              <div className="options-page-line-input fill-width-on-small-screen">
                <TextField
                  label="Altezza del video (px)"
                  placeholder="Altezza Video"
                  type="number"
                  defaultValue={SettingsUtil.GetSettings(
                    "h_video_m",
                    DEFAULT_VIDEO_SIZES[1]
                  )}
                  onChange={(e: any, t: string | undefined) => {
                    if (t !== undefined) {
                      SettingsUtil.SetSettings("h_video_m", +t);
                    }
                  }}
                />
              </div>
              <div className="options-page-line-input fill-width-on-small-screen">
                <TextField
                  label="Larghezza nella finestra grande (px)"
                  placeholder="Ampiezza Video (In Modale)"
                  type="number"
                  defaultValue={SettingsUtil.GetSettings(
                    "w_video_d",
                    DEFAULT_VIDEO_SIZES[2]
                  )}
                  onChange={(e: any, t: string | undefined) => {
                    if (t !== undefined) {
                      SettingsUtil.SetSettings("w_video_d", +t);
                    }
                  }}
                />
              </div>
              <div className="options-page-line-input fill-width-on-small-screen">
                <TextField
                  label="Altezza nella finestra grande (px)"
                  placeholder="Altezza Video (In Modale)"
                  type="number"
                  defaultValue={SettingsUtil.GetSettings(
                    "h_video_d",
                    DEFAULT_VIDEO_SIZES[3]
                  )}
                  onChange={(e: any, t: string | undefined) => {
                    if (t !== undefined) {
                      SettingsUtil.SetSettings("h_video_d", +t);
                    }
                  }}
                />
              </div>
              <div className="options-page-line-input-button fill-width-on-small-screen">
                <PrimaryButton
                  text="Ripristina misure"
                  onClick={() => {
                    SettingsUtil.SetSettings(
                      "w_video_m",
                      DEFAULT_VIDEO_SIZES[0]
                    );
                    SettingsUtil.SetSettings(
                      "h_video_m",
                      DEFAULT_VIDEO_SIZES[1]
                    );
                    SettingsUtil.SetSettings(
                      "w_video_d",
                      DEFAULT_VIDEO_SIZES[2]
                    );
                    SettingsUtil.SetSettings(
                      "h_video_d",
                      DEFAULT_VIDEO_SIZES[3]
                    );
                    ToastMessage(
                      "info",
                      "Proporzioni video ripristinate ai default."
                    );
                    doReload();
                  }}
                />
              </div>
            </div>
          )}
        </details>
        )}
        {!reload && adminUI && (
          <div className="panel">
            <strong>Impostazioni avanzate del sistema</strong>
            <div>Parametri di funzionamento di tutto il sistema.</div>
            <br />
            <MessageBar messageBarType={MessageBarType.warning}>
              Attenzione: questi parametri{" "}
              <strong>sono cruciali al funzionamento del sistema </strong>.
              Impostazioni errate possono portare al blocco dell'applicativo in
              parte o nella sua interezza.
            </MessageBar>
            <br />
            <div>
              <Toggle
                defaultChecked={developerUI()}
                onChange={(e, check: boolean | undefined) => {
                  localStorage.setItem(
                    "developer-ui",
                    check ? "true" : "false"
                  );
                }}
                onText="Funzioni per sviluppatori: accese"
                offText="Funzioni per sviluppatori: spente"
              />
            </div>
            <br />

            <ModalDialog
              loadingButtons={loading}
              modalTitle={
                openModalEdit
                  ? "Modifica impostazione"
                  : "Aggiungi impostazione"
              }
              enableModal={openModal}
              onAbort={() => {
                setOpenModal(false);
              }}
              onAccept={() => {
                createOrEditSetting();
              }}
              modalInnerComponent={
                <div>
                  <TextField
                    label="Chiave"
                    required
                    defaultValue={targetSetting ? targetSetting.key : ""}
                    onChange={(e, t) => {
                      if (t !== undefined) {
                        updateSettingItem("key", t);
                      }
                    }}
                  />
                  <TextField
                    label="Valore"
                    required
                    multiline={
                      targetSetting &&
                      (targetSetting.value.startsWith("{") ||
                        targetSetting.value.startsWith("["))
                        ? true
                        : false
                    }
                    rows={10}
                    defaultValue={targetSetting ? targetSetting.value : ""}
                    onChange={(e, t) => {
                      if (t !== undefined) {
                        updateSettingItem("value", t);
                      }
                    }}
                  />
                  <Toggle
                    label={"Abilitato"}
                    onChange={(e, t) => {
                      if (t !== undefined) {
                        updateSettingItem("enabled", t);
                      }
                    }}
                    defaultChecked={
                      targetSetting ? targetSetting.enabled : false
                    }
                    offText="No"
                    onText="Si"
                  />
                </div>
              }
            />
            <DataTable
              extraInnerProps={{
                allowFilter: true,
                extraHeaderItems: [
                  <PrimaryButton
                    iconProps={{ iconName: "Add" }}
                    text={"Aggiungi impostazione"}
                    onClick={() => {
                      setOpenModalEdit(false);
                      setTargetSetting({
                        enabled: false,
                        value: "",
                        key: "",
                      });
                      setOpenModal(true);
                    }}
                  ></PrimaryButton>,
                ],
              }}
              columns={[
                {
                  name: "Modifica",
                  fieldName: "type",
                  dataType: "string",
                  size: "medium",
                  onRender: (item: any) => {
                    return (
                      <div>
                        <PrimaryButton
                          iconProps={{ iconName: "Edit" }}
                          onClick={() => {
                            setOpenModalEdit(true);
                            setTargetSetting(item);
                            setOpenModal(true);
                          }}
                          text="Modifica"
                        ></PrimaryButton>
                      </div>
                    );
                  },
                },
                {
                  name: "Chiave",
                  fieldName: "key",
                  dataType: "string",
                  size: "large",
                },
                {
                  name: "Valore",
                  fieldName: "value",
                  dataType: "string",
                  size: "large",
                },
                {
                  name: "Abilitato",
                  fieldName: "enabled",
                  dataType: "string",
                  size: "small",
                  onRender: (item: any) => {
                    return <div>{item.enabled ? "Si" : "No"}</div>;
                  },
                },
              ]}
              rowData={sysSettings}
            />
          </div>
        )}

        {adminUI && <div className="panel" style={{ marginTop: "1em" }}>
          <strong>Informazioni di sistema</strong>
          <div style={{ marginTop: "0.5em", fontSize: "0.9em", opacity: 0.7 }}>
            <div>Frontend: v{process.env.REACT_APP_VERSION}</div>
            <div>Backend: v{backendVersion}</div>
          </div>
        </div>}
      </div>
    </div>
  );
};

export default OptionsPage;
